import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

const HUB_URL = process.env.REACT_APP_HUB_URL || 'https://agi-internal-hub.pages.dev';
const IDLE_TIMEOUT_MS = 12 * 60 * 60 * 1000; // 12 hours

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // ── Fetch profile from Supabase profiles table ──
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        console.error('Error fetching profile:', error.message || error);
        return null;
      }
      return data;
    } catch (err) {
      console.error('Profile fetch error:', err.message || err);
      return null;
    }
  }, []);

  // ── When user changes, fetch their profile ──
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoaded(false);
      return;
    }

    let cancelled = false;
    setProfileLoaded(false);

    const loadProfile = async (retries = 3) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (retries > 0) {
          await new Promise((r) => setTimeout(r, 500));
          if (!cancelled) return loadProfile(retries - 1);
        }
        if (!cancelled) setProfileLoaded(true);
        return;
      }

      const prof = await fetchProfile(user.id);
      if (cancelled) return;

      if (prof && !prof.is_active) {
        setUser(null);
        setProfile(null);
        supabase.auth.signOut({ scope: 'local' });
      } else {
        setProfile(prof);
      }
      setProfileLoaded(true);
    };

    loadProfile();
    return () => { cancelled = true; };
  }, [user, fetchProfile]);

  // ── Initialize auth once on mount (sequential, like RMA portal) ──
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        // STEP 1: Check URL for tokens from AGI Hub
        const params = new URLSearchParams(window.location.search);
        const accessToken = params.get('access_token') || params.get('token');
        const refreshToken = params.get('refresh_token') || '';

        if (accessToken) {
          // Remember that this session was launched from the Hub
          sessionStorage.setItem('launched_from_hub', 'true');

          // STEP 2: Clean URL immediately
          window.history.replaceState(null, '', window.location.pathname);

          // STEP 3: Establish session with Supabase
          if (refreshToken) {
            const { data: { session }, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) {
              console.error('setSession failed:', error.message);
            } else if (session?.user && isMounted) {
              setUser(session.user);
            }
          } else {
            // Legacy: access_token only — validate user
            const { data: { user: tokenUser }, error } = await supabase.auth.getUser(accessToken);
            if (!error && tokenUser && isMounted) {
              setUser(tokenUser);
            }
          }
        } else {
          // No URL tokens — check for existing session in localStorage
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user && isMounted) {
            setUser(session.user);
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes (sign-in from another tab, token refresh, etc.)
    // This does NOT run during initAuth because initAuth is fully awaited above.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Only react to real changes, not the initial session load
        if (event === 'INITIAL_SESSION') return;
        setUser(session?.user ?? null);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ── Inactivity timeout (pauses while tab is hidden) ──
  useEffect(() => {
    if (!user) return;

    let timer;
    let hiddenAt = null;
    let remainingWhenHidden = IDLE_TIMEOUT_MS;

    const resetTimer = () => {
      if (document.hidden) return; // Don't reset while hidden
      clearTimeout(timer);
      remainingWhenHidden = IDLE_TIMEOUT_MS;
      timer = setTimeout(() => {
        supabase.auth.signOut({ scope: 'local' }).finally(() => {
          window.location.replace(HUB_URL);
        });
      }, IDLE_TIMEOUT_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden — pause the timer, record how much time was left
        hiddenAt = Date.now();
        clearTimeout(timer);
      } else {
        // Tab visible again — resume with remaining time
        if (hiddenAt !== null) {
          // Don't count hidden time — just restart with whatever was remaining
          clearTimeout(timer);
          timer = setTimeout(() => {
            supabase.auth.signOut({ scope: 'local' }).finally(() => {
              window.location.replace(HUB_URL);
            });
          }, remainingWhenHidden);
          hiddenAt = null;
        }
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // ── Actions ──
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setUser(data.user);
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
    setUser(null);
    setProfile(null);
  };

  const exitToHub = () => {
    sessionStorage.removeItem('launched_from_hub');
    // Don't call signOut — it revokes the session on the server, which
    // invalidates the Hub's stored tokens and breaks re-launch.
    window.location.replace(HUB_URL);
  };

  const handleExit = () => {
    const fromHub = sessionStorage.getItem('launched_from_hub');
    sessionStorage.removeItem('launched_from_hub');
    if (fromHub) {
      // Just navigate back — keep server session alive for re-launch
      window.location.replace(HUB_URL);
    } else {
      supabase.auth.signOut({ scope: 'local' }).finally(() => {
        window.location.replace('/');
      });
    }
  };

  const launchedFromHub = !!sessionStorage.getItem('launched_from_hub');

  const getAccessToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  };

  const isSuperAdmin = profile?.role === 'superadmin';
  const isAdmin = profile?.role === 'admin' || isSuperAdmin;
  const isStaff = profile?.role === 'staff';

  const value = {
    user,
    profile,
    loading,
    profileLoaded,
    signIn,
    signOut,
    exitToHub,
    handleExit,
    launchedFromHub,
    getAccessToken,
    isSuperAdmin,
    isAdmin,
    isStaff,
    isAuthenticated: !!user && !!profile?.is_active,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
